import { useEffect, useState } from 'react';
import { safetyKitApi, stationeryApi, itApi, siteApi, materialUnitApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import SearchableSelect from './SearchableSelect';
import MaterialInfoPanel from './MaterialInfoPanel';
import LoadingSpinner from './LoadingSpinner';

// "Item Lookup" — pick ONE Material/Stationery/IT item and see it across
// every site (or one selected site) at once: every serialized unit + who
// currently holds it (for requiresMaterialId items), or just aggregate
// in-stock/issued totals per site (for everything else). Shared between the
// standalone Item Lookup page and the Dashboard, which embeds this same
// component directly rather than duplicating the picker/table logic.
const STATUS_STYLES = {
  IN_STOCK: 'bg-green-100 text-green-700',
  ISSUED: 'bg-amber-100 text-amber-700',
  DAMAGED: 'bg-red-100 text-red-700',
  DISPOSED: 'bg-gray-200 text-gray-600',
  PENDING_DAMAGE_APPROVAL: 'bg-orange-100 text-orange-700',
  PENDING_REPAIR: 'bg-purple-100 text-purple-700',
  UNDER_MAINTENANCE: 'bg-blue-100 text-blue-700',
};

const IDFIELD = { material: 'kitId', stationery: 'stationeryItemId', it: 'itItemId' };

// Local unit-detail modal — same pattern deliberately duplicated across the
// app (InventoryPage.jsx, AssetHistory.jsx, etc.) rather than extracted into
// a shared component; small and self-contained.
function UnitDetailModal({ historyModal, onClose }) {
  if (!historyModal) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Material Details</h3>
            {historyModal !== 'loading' && (
              <p className="text-sm text-gray-500 mt-0.5">{historyModal.unit?.materialIdCode}</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4">×</button>
        </div>
        {historyModal === 'loading' ? (
          <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              {historyModal.unit?.kit?.name || historyModal.unit?.stationeryItem?.name || historyModal.unit?.itItem?.name} · {historyModal.unit?.site?.name}
            </p>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {historyModal.events.map((e) => (
                <div key={e.id} className="px-3 py-2 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{e.eventType.replace(/_/g, ' ')}</p>
                    {e.remarks && <p className="text-xs text-gray-500 mt-0.5">{e.remarks}</p>}
                    {e.performedByUsername && <p className="text-xs text-gray-400 mt-0.5">by {e.performedByUsername}</p>}
                  </div>
                  <p className="text-xs text-gray-400 whitespace-nowrap">{new Date(e.occurredAt).toLocaleString('en-GB')}</p>
                </div>
              ))}
              {historyModal.events.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">No history recorded yet</p>
              )}
            </div>
            <MaterialInfoPanel
              materialUnitId={historyModal.unit?.id}
              unitKitId={historyModal.unit?.kitId}
              unitItItemId={historyModal.unit?.itItemId}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default function ItemLookupPanel({ compact = false }) {
  const { hasPermission, isSiteRestricted } = useAuth();
  const canViewIT = hasPermission('it_view');
  // Item Lookup is its own permission, independent of inventory_view — a
  // user without it sees nothing here, whether this panel is rendered on
  // its own page or embedded in the Dashboard.
  const canViewItemLookup = hasPermission('item_lookup_view');

  const [itemType, setItemType] = useState('material');
  const [materials, setMaterials] = useState([]);
  const [stationeryItems, setStationeryItems] = useState([]);
  const [itItems, setItItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [siteId, setSiteId] = useState('ALL');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [historyModal, setHistoryModal] = useState(null);

  useEffect(() => {
    if (!canViewItemLookup) return;
    const requests = [safetyKitApi.list(), stationeryApi.listItems({ isActive: true }), siteApi.list()];
    if (canViewIT) requests.push(itApi.listItems({ isActive: true }));
    Promise.all(requests)
      .then(([m, s, sites_, i]) => {
        setMaterials(m.data.data || []);
        setStationeryItems(s.data.data || []);
        setSites(sites_.data.data || []);
        if (i) setItItems(i.data.data || []);
      })
      .catch(() => {});
  }, [canViewIT, canViewItemLookup]);

  const itemsForType = itemType === 'material' ? materials : itemType === 'stationery' ? stationeryItems : itItems;

  useEffect(() => {
    setSelectedItemId('');
    setOverview(null);
  }, [itemType]);

  useEffect(() => {
    if (!canViewItemLookup || !selectedItemId) { setOverview(null); return; }
    setLoading(true);
    setError('');
    const params = { [IDFIELD[itemType]]: selectedItemId };
    if (siteId !== 'ALL') params.siteId = siteId;
    materialUnitApi.overview(params)
      .then((res) => setOverview(res.data.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load item overview'))
      .finally(() => setLoading(false));
  }, [canViewItemLookup, itemType, selectedItemId, siteId]);

  if (!canViewItemLookup) return null;

  const openUnitDetail = async (unitId) => {
    setHistoryModal('loading');
    try {
      const res = await materialUnitApi.history(unitId);
      setHistoryModal(res.data.data);
    } catch (err) {
      setHistoryModal(null);
      setError(err.response?.data?.message || 'Error loading unit history');
    }
  };

  return (
    <div className={compact ? '' : 'card'}>
      {!compact && (
        <>
          <h3 className="text-sm font-semibold text-slate-700 mb-0.5">Item Lookup</h3>
          <p className="text-xs text-slate-400 mb-4">Pick one item to see it across every site at once</p>
        </>
      )}

      <div className="flex gap-1 mb-3">
        {[['material', 'Material'], ['stationery', 'Stationery'], ...(canViewIT ? [['it', 'IT']] : [])].map(([v, l]) => (
          <button
            key={v}
            type="button"
            className={itemType === v ? 'btn-primary text-sm' : 'btn-secondary text-sm'}
            onClick={() => setItemType(v)}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Item</label>
          <SearchableSelect
            options={itemsForType.map((it) => ({ id: it.id, label: it.name }))}
            value={selectedItemId}
            onChange={setSelectedItemId}
            placeholder="Search item…"
          />
        </div>
        {!isSiteRestricted && (
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Site</label>
            <SearchableSelect
              options={[{ id: 'ALL', label: 'All Sites' }, ...sites.map((s) => ({ id: s.id, label: s.name }))]}
              value={siteId}
              onChange={setSiteId}
              placeholder="All Sites"
            />
          </div>
        )}
      </div>

      {error && <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">{error}</p>}

      {!selectedItemId ? (
        <p className="text-sm text-gray-400 text-center py-8">Select an item to see its details</p>
      ) : loading ? (
        <LoadingSpinner />
      ) : overview?.requiresMaterialId ? (
        <>
          {(() => {
            const withId = overview.units.length;
            const withoutId = (overview.untrackedBySite || []).reduce((s, r) => s + r.count, 0);
            const issued = overview.units.filter((u) => u.status === 'ISSUED').length;
            // Untracked stock has no unit row to carry a status, but it can
            // only ever be sitting in inventory — requiresMaterialId items
            // must have a specific unit selected to be issued, so untracked
            // quantity is inherently "in stock", same as a tracked unit
            // whose own status is IN_STOCK.
            const inStock = overview.units.filter((u) => u.status === 'IN_STOCK').length + withoutId;
            return (
              <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <div className="flex flex-wrap gap-4 text-sm">
                  {/* With/Without ID is only meaningful once there's actually
                      some untracked stock to distinguish from — when every
                      unit already has a Material ID, that split is moot. */}
                  {withoutId > 0 && (
                    <>
                      <span className="text-amber-800">With ID: <span className="font-semibold">{withId}</span></span>
                      <span className="text-amber-800">Without ID: <span className="font-semibold">{withoutId}</span></span>
                    </>
                  )}
                  <span className="text-amber-800">In Stock: <span className="font-semibold">{inStock}</span></span>
                  <span className="text-amber-800">Issued: <span className="font-semibold">{issued}</span></span>
                  <span className="text-amber-800">Total: <span className="font-semibold">{withId + withoutId}</span></span>
                </div>
              </div>
            );
          })()}
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Material ID</th>
                  <th>Site</th>
                  <th>Status</th>
                  <th>Held By</th>
                </tr>
              </thead>
              <tbody>
                {overview.units.map((u) => (
                  <tr key={u.id}>
                    <td className="font-mono text-sm">
                      <button className="text-indigo-600 hover:underline" onClick={() => openUnitDetail(u.id)}>{u.materialIdCode}</button>
                    </td>
                    <td className="text-sm">{u.site?.name || '—'}</td>
                    <td>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES[u.status] || 'bg-gray-100 text-gray-600'}`}>
                        {u.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="text-sm">
                      {u.heldBy ? <>{u.heldBy.name}{u.heldBy.employeeId ? ` (${u.heldBy.employeeId})` : ''}</> : <span className="text-gray-400">In Stock</span>}
                    </td>
                  </tr>
                ))}
                {overview.units.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-6 text-gray-400">No units found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : overview ? (
        <div className="table-container">
          <table>
            <thead>
              <tr><th>Site</th><th>In Stock</th><th>Issued</th></tr>
            </thead>
            <tbody>
              {overview.bySite.map((r) => (
                <tr key={r.site.id}>
                  <td className="font-medium">{r.site.name}</td>
                  <td className="font-semibold">{r.inStock}</td>
                  <td className="font-semibold text-amber-600">{r.issued}</td>
                </tr>
              ))}
              {overview.bySite.length === 0 && (
                <tr><td colSpan={3} className="text-center py-6 text-gray-400">No stock or issuances found</td></tr>
              )}
              {overview.bySite.length > 1 && (
                <tr className="border-t-2 border-gray-200 font-bold">
                  <td>Total</td>
                  <td>{overview.bySite.reduce((s, r) => s + r.inStock, 0)}</td>
                  <td className="text-amber-600">{overview.bySite.reduce((s, r) => s + r.issued, 0)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      <UnitDetailModal historyModal={historyModal} onClose={() => setHistoryModal(null)} />
    </div>
  );
}
